import React from "react";

interface AsyncHandlerOptions<TStatus = unknown> {
  onError?: (error: Error) => void;
  onSuccess?: (result?: unknown) => void;
  setStatus?: React.Dispatch<React.SetStateAction<TStatus>>;
  setError?: React.Dispatch<React.SetStateAction<string | null>>;
  errorStatus?: TStatus;
  loadingStatus?: TStatus;
}

export const asyncHandler = <T = unknown, TStatus = unknown>(
  fn: () => Promise<T>,
  options: AsyncHandlerOptions<TStatus> = {},
) => {
  const {
    onError,
    onSuccess,
    setStatus,
    setError,
    errorStatus,
    loadingStatus,
  } = options;

  // Set loading status if provided
  if (loadingStatus && setStatus) {
    setStatus(loadingStatus);
  }

  return Promise.resolve(fn())
    .then((result) => {
      if (onSuccess) {
        onSuccess(result);
      }
      return result;
    })
    .catch((error) => {
      // Set error status and message
      if (setStatus && errorStatus !== undefined) {
        setStatus(errorStatus);
      }

      if (setError) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An unexpected error occurred";
        setError(errorMessage);
      }

      // Call custom error handler if provided
      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }

      // Re-throw if no error handlers provided
      if (!onError && !setStatus && !setError) {
        throw error;
      }
    });
};
